/**
 * Add Job From Email - Instruction Lightbox
 * Shows instructions for forwarding job alert emails to SkillSnap
 */
import { useState, useRef } from 'react';
import { X, Copy, Check } from 'lucide-react';
import { useEscapeClose } from '../hooks/useEscapeClose';
import { useFocusTrap } from '../hooks/useFocusTrap';

interface AddJobFromEmailProps {
  onClose: () => void;
}

const EMAIL_ADDRESS = 'jobs@skillsnap.me';

export function AddJobFromEmail({ onClose }: AddJobFromEmailProps) {
  const [copied, setCopied] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  useEscapeClose(onClose);
  useFocusTrap(modalRef, true);

  function handleCopyEmail() {
    navigator.clipboard.writeText(EMAIL_ADDRESS).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" role="dialog" aria-modal="true" aria-label="Add Jobs from Email">
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Add Jobs from Email</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" aria-label="Close">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-5">
          <p className="text-sm font-semibold text-gray-800">3 easy steps:</p>

          <ol className="space-y-3 text-sm text-gray-700">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-bold">1</span>
              <span>Sign up for recurring "job alert" emails at LinkedIn, Indeed, etc.</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-bold">2</span>
              <span>When you get an email with interesting jobs, forward that email to <strong className="text-primary-600">{EMAIL_ADDRESS}</strong></span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-bold">3</span>
              <span>You will get an email back from {EMAIL_ADDRESS}, confirming those jobs were added to your SkillSnap queue</span>
            </li>
          </ol>

          {/* Copy email button */}
          <div className="flex justify-center">
            <button
              onClick={handleCopyEmail}
              data-help-target="mainapp.addjob.fromemail.email"
              className="flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-700 border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4" />
                  <span className="text-sm font-medium">Copied!</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  <span className="text-sm font-medium">{EMAIL_ADDRESS}</span>
                </>
              )}
            </button>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 text-xs text-gray-600 space-y-2">
            <p>
              SkillSnap parses the jobs, visits all embedded links from your forwarded email, assembles a comprehensive job description for every job it finds, and places completed jobs in your SkillSnap Queue for your review. Easy, secure, and completely under your control!
            </p>
            <p>
              If you want this to be automatic, simply set up an auto-forward rule using your email provider.
            </p>
            <p className="font-medium text-gray-700">
              Tip: make sure the forwarded email is the same as your SkillSnap.me email. SkillSnap will only process emails that are FROM a known email.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-4 border-t">
          <button
            onClick={onClose}
            data-help-target="mainapp.addjob.fromemail.gotit"
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
